import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { RestaurantTable, TableFormData } from '../types/admin';

export function useRestaurantTables(areaId?: string) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['pos-tables', company?.id, areaId],
    queryFn: async (): Promise<RestaurantTable[]> => {
      if (!company?.id) return [];

      let query = supabase
        .from('pos_tables')
        .select('*')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('table_number', { ascending: true });

      if (areaId) {
        query = query.eq('area_id', areaId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id,
  });
}

export function useCreateTable() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (data: TableFormData & { area_id: string }) => {
      if (!company?.id) throw new Error('No company selected');

      const { data: table, error } = await supabase
        .from('pos_tables')
        .insert({
          company_id: company.id,
          area_id: data.area_id,
          table_number: data.table_number,
          table_name: data.table_name,
          seats: data.seats,
          shape: data.shape,
          position_x: 0,
          position_y: 0,
          width: 100,
          height: 100,
          rotation: 0,
          status: 'available',
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return table;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}

export function useUpdateTable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<RestaurantTable> }) => {
      const { data: table, error } = await supabase
        .from('pos_tables')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return table;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}

export function useDeleteTable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('pos_tables')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}

export function useUpdateTableStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      orderId 
    }: { 
      id: string; 
      status: 'available' | 'occupied' | 'reserved' | 'cleaning';
      orderId?: string | null;
    }) => {
      const { data: table, error } = await supabase
        .from('pos_tables')
        .update({ 
          status,
          current_order_id: orderId || null,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return table;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}
